import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, Sparkles, MapPin } from "lucide-react";
import api from "@/lib/api";
import { useLocation } from "@/context/LocationContext";
import { ProductCard } from "@/components/ProductCard";
import { SellerCard } from "@/components/SellerCard";
import { CatIcon } from "@/lib/categoryIcons";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";

const HERO = "https://static.prod-images.emergentagent.com/jobs/8d225509-02a4-4986-b162-0c51d7cb4421/images/248d5d3d74c30f460d53a0a1027fe5e4254a8e494539184d57a7d66b10272638.jpeg";

export default function HomePage() {
  const navigate = useNavigate();
  const { loc } = useLocation();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    const geo = loc ? `&lat=${loc.lat}&lng=${loc.lng}` : "";
    api.get("/categories").then((r) => setCategories(r.data));
    api.get(`/products?sort=${loc ? "distance" : "recent"}&limit=8${geo}`).then((r) => setProducts(r.data));
    api.get(`/sellers?${geo.slice(1)}`).then((r) => setSellers(r.data.slice(0, 4)));
  }, [loc]);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#E5DEC9]/60 bg-gradient-to-b from-[#FAF7F2] via-[#F4EFE6] to-[#FAF7F2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div className="animate-fade-up">
            <span className="bg-[#1A3C2A]/10 text-[#1A3C2A] px-3.5 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-2">
              <Sparkles size={13} /> Your neighbourhood marketplace
            </span>
            <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-[#1A3C2A] mt-4">
              Buy local.<br />Sell local.<br /><span className="text-[#E07A5F]">Support your community.</span>
            </h1>
            <p className="text-base sm:text-lg text-[#5A626A] mt-5 max-w-md">
              Discover fresh produce, homemade food and handmade crafts from people and small businesses right in your neighbourhood.
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Button data-testid="hero-shop-local" onClick={() => navigate("/browse")}
                className="rounded-full bg-[#1A3C2A] hover:bg-[#132E20] text-white px-6 h-12 font-semibold shadow-md shadow-[#1A3C2A]/15">
                Shop Local <ArrowRight size={16} className="ml-1" />
              </Button>
              <Button data-testid="hero-sell-nabor" onClick={() => navigate("/sell")}
                className="rounded-full bg-[#E07A5F] hover:bg-[#D06447] text-white px-6 h-12 font-semibold shadow-md shadow-[#E07A5F]/20">
                Sell on Nabor
              </Button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); navigate(`/browse?q=${encodeURIComponent(q)}`); }}
              className="mt-7 relative max-w-md">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9A9282]" />
              <input data-testid="hero-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="What are you looking for?"
                className="w-full pl-11 pr-28 py-3.5 rounded-full bg-white border border-[#E5DEC9] text-sm focus:outline-none focus:ring-2 focus:ring-[#E07A5F]/50 shadow-sm" />
              <Button type="submit" data-testid="hero-search-submit"
                className="absolute right-1.5 top-1.5 rounded-full bg-[#1A3C2A] hover:bg-[#132E20] h-9 px-5 text-sm">Search</Button>
            </form>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <img src={HERO} alt="Local neighbourhood marketplace"
              className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/3]" />
            <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-lg px-4 py-3 border border-[#E5DEC9] hidden sm:flex items-center gap-2">
              <MapPin size={18} className="text-[#E07A5F]" />
              <div>
                <p className="text-xs text-[#5A626A]">Available near you</p>
                <p className="font-display font-bold text-[#1A3C2A] text-sm">{sellers.length} local sellers</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#E07A5F]">Explore</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1A3C2A] mt-1">Shop by category</h2>
          </div>
          <Link to="/browse" className="text-sm font-semibold text-[#1A3C2A] hover:text-[#E07A5F] flex items-center gap-1">All <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
          {categories.map((c) => (
            <button key={c.id} data-testid={`category-card-${c.id}`}
              onClick={() => navigate(`/browse?category=${encodeURIComponent(c.name)}`)}
              className="bg-white border border-[#E5DEC9] rounded-2xl p-4 flex flex-col items-center gap-2 hover:border-[#1A3C2A]/40 hover:shadow-md hover:-translate-y-0.5 transition-all">
              <div className="w-11 h-11 rounded-xl bg-[#1A3C2A]/8 flex items-center justify-center text-[#1A3C2A]">
                <CatIcon name={c.icon} size={22} />
              </div>
              <span className="text-xs sm:text-sm font-semibold text-[#22252A] text-center leading-tight">{c.name}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Popular near you */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#E07A5F]">Fresh picks</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1A3C2A] mt-1">Popular near you</h2>
          </div>
          <Link to="/browse" data-testid="see-all-products" className="text-sm font-semibold text-[#1A3C2A] hover:text-[#E07A5F] flex items-center gap-1">See all <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* Meet local sellers */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#E07A5F]">Community</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#1A3C2A] mt-1">Meet local sellers</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {sellers.map((s, i) => <SellerCard key={s.id} seller={s} index={i} />)}
        </div>
      </section>

      {/* Seller CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="bg-[#1A3C2A] rounded-3xl px-6 sm:px-12 py-10 sm:py-14 relative overflow-hidden">
          <div className="absolute -right-10 -top-10 w-56 h-56 bg-[#E07A5F]/20 rounded-full blur-3xl" />
          <div className="relative max-w-xl">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#E07A5F]">Have something to sell?</p>
            <h2 className="font-display text-2xl sm:text-4xl font-extrabold text-white mt-2 leading-tight">Turn your local business, hobby or homemade products into an online shop.</h2>
            <p className="text-[#FAF7F2]/80 mt-4">Set up your Nabor storefront in minutes and start reaching customers in your neighbourhood.</p>
            <Button data-testid="cta-start-selling" onClick={() => navigate("/sell")}
              className="mt-6 rounded-full bg-[#E07A5F] hover:bg-[#D06447] text-white px-7 h-12 font-semibold">
              Start Selling <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
